import React, { FunctionComponent, MouseEventHandler, useEffect, useState } from 'react';
import landingStyles from './styles/cpua4769landing.scss';
import logo from '../../assets/images/rtspro-logo-blue.svg';
import { Button, Snackbar } from 'shamrock-clover-ui';
import Login from '../login/Login';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { getAirshipChannelId } from '../../utils/airshipHandler';
import { RootState } from 'bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { EasterEggWrapper } from '../../utils/easterEgg';
import { setFeatures } from '../../redux/actions/appActions';
import { useAuth0 } from '@auth0/auth0-react';
import { AUTH0_ERRORS, FF } from '../../utils/utils';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const Config = require('Config');

const languageDict = { en: 'english', es: 'spanish' };

interface LandingProps {
  handleShowAsSupportPage: MouseEventHandler;
  sendAnalyticsEvent: (arg: string) => void;
  setRerender: React.Dispatch<React.SetStateAction<number>>;
}

const CPUA4769Landing: FunctionComponent<LandingProps> = ({
  handleShowAsSupportPage,
  sendAnalyticsEvent,
  setRerender,
}) => {
  const { t, i18n } = useTranslation();
  const features = useSelector<RootState, string[]>((state) => state.app.features);
  const dispatch = useDispatch();
  const { loginWithRedirect } = useAuth0();

  const enableAuth0EasterEgg = features.includes('2f5cf2fb1aac9f6c11ca8fdfef53c3802159a81f87242a58813340a6f808c277');
  const enableAuth0 = features.includes(FF.AUTH0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [searchParams] = useSearchParams();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const triggerLoginModal = () => setShowLoginModal(!showLoginModal);
  const handleRedirectToLogin = () => {
    sendAnalyticsEvent('login');
    if (enableAuth0) {
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname,
        },
      });
    } else {
      triggerLoginModal();
    }
  };

  const handleRedirectToSignUp = () => {
    sendAnalyticsEvent('createAccount');
    window.location.href = `${Config.registerUrl}`;
  };

  const showSnackbarError = (msg: string) => {
    setSnackbarMessage(msg);
    setShowSnackbar(true);
  };

  useEffect(() => {
    if (searchParams.get('error_description') === AUTH0_ERRORS.USER_BLOCKED) {
      showSnackbarError(t('account_locked_login_response'));
      const cleanURL = location.pathname;
      if (location.search) {
        navigate(cleanURL, { replace: true });
      }
    }
  }, []);

  const handleRedirectToCreditTrial = async () => {
    sendAnalyticsEvent('createAccount');
    const deviceId = uuidv4();
    localStorage.setItem('device_id', deviceId);
    const airshipChannelId = await getAirshipChannelId();
    const languageAbr = i18n.language as 'en' | 'es';
    const language = Reflect.get<typeof languageDict, 'en' | 'es'>(languageDict, languageAbr);
    const deviceType = 'web';
    const payload: Record<string, string> = {
      deviceId,
      language,
      deviceType,
    };
    if (airshipChannelId) payload.airshipChannelId = airshipChannelId;
    const params = new URLSearchParams(payload);
    const url = new URL(`${Config.registerUrl}/trial-service?${params}`);
    window.location.href = url.href;
  };

  return (
    <React.Fragment>
      {showLoginModal && (
        <Login closeLogin={triggerLoginModal} sendAnalyticsEvent={sendAnalyticsEvent} setRerender={setRerender} />
      )}
      <div className={landingStyles.rtsProLogo}>
        <img src={logo} alt="rtspro_blue_logo" />
      </div>
      <div className={landingStyles.welcome}>
        <div className={landingStyles.welcomeInner}>
          <h2 data-testid="welcome-header">{t('welcome')}!</h2>
          <p>{t('landing_subheading')}</p>
        </div>
      </div>
      <div className={landingStyles.buttonContainer}>
        <div className={landingStyles.callToAction}>
          <Button
            buttonStyle="outlined"
            onClick={handleRedirectToSignUp}
            width="176px"
            height="44px"
            data-testid="create-account-button"
          >
            <span className={landingStyles.buttonText}>{t('create_acct')}</span>
          </Button>
        </div>
        <div className={landingStyles.callToAction}>
          <Button onClick={handleRedirectToLogin} width="176px" height="44px">
            <span className={landingStyles.buttonText} data-testid="log-in-button">
              {t('login')}
            </span>
          </Button>
        </div>
        <span className={landingStyles.separator} />
        <div className={landingStyles.freeTrialContainer}>
          <p className={landingStyles.freeTrialText}>{t('landing.free_trial')}</p>
          <p className={landingStyles.freeTrialDescriptionText}>{t('landing.free_trial_description')}</p>
          <Button buttonStyle="outlined" onClick={handleRedirectToCreditTrial} width="130px" height="44px">
            <span className={landingStyles.trialButtonText}>{t('landing.start_free_trial')}</span>
          </Button>
        </div>
        <div className={landingStyles.questions}>
          <EasterEggWrapper
            featureToSet={FF.AUTH0}
            features={features}
            actionCreator={setFeatures}
            dispatch={dispatch}
            isEasterEggEnabled={enableAuth0EasterEgg}
          >
            <span>{t('questions')}</span>
          </EasterEggWrapper>
          <a onClick={handleShowAsSupportPage} data-testid="contact-us-link">
            {t('contact_us')}
          </a>
        </div>
      </div>
      <div className="">
        <Snackbar
          open={showSnackbar}
          duration={5000}
          message={snackbarMessage}
          variant={'error'}
          position="bottomCenter"
          onClose={() => setShowSnackbar(false)}
        />
      </div>
    </React.Fragment>
  );
};

export default CPUA4769Landing;
